<template>
  <div>
    <Head title="Create Item Asset" />
    <h1 class="mb-8 text-3xl font-bold">
      Inventaris
      <span class="text-indigo-400 font-medium"> / </span>
      <Link class="text-indigo-400 hover:text-indigo-600" href="/item_assets">
        Item Asset
      </Link>
      <span class="text-indigo-400 font-medium"> / </span> Create
    </h1>
    <div class="max-w-3xl bg-white rounded-md shadow overflow-hidden">
      <form @submit.prevent="store">
        <div class="flex flex-wrap -mb-8 -mr-6 p-8">
          <div class="pb-8 pr-6 w-full md:w-3/5">
            <label class="form-label">Pilih Item:</label>
            <vSelect
              v-model="form.mst_item_id"
              class="focus:text-indigo-500"
              :class="{ error: form.errors.mst_item_id }"
              placeholder="[Pick Item...]"
              label="name"
              :reduce="(option) => option.id"
              :filterable="false"
              :options="itemOptions"
              :append-to-body="true"
              @search="onSearch" />
            <div v-if="form.errors.mst_item_id" class="form-error whitespace-normal">
              {{ form.errors.mst_item_id }}
            </div>
          </div>
          <div class="w-full" />
          <text-input
            v-model="form.serial_num"
            :error="form.errors.serial_num"
            class="pb-8 pr-6 w-1/2"
            label="No. Seri" />
          <select-input
            v-model="form.condition"
            :error="form.errors.condition"
            class="pb-8 pr-6 w-full md:w-1/3"
            label="Kondisi Asset">
            <option value="NEW">
              BARU
            </option>
            <option value="USED">
              BEKAS
            </option>
          </select-input>
          <select-input
            v-model="form.status"
            :error="form.errors.status"
            class="pb-8 pr-6 w-full md:w-1/3"
            label="Status Asset">
            <option value="ACTIVE">
              AKTIF
            </option>
            <option value="ELIMINATED">
              NON-AKTIF
            </option>
          </select-input>
          <select-input
            v-model="form.asset_ownership"
            :error="form.errors.asset_ownership"
            class="pb-8 pr-6 w-full md:w-1/3"
            label="Owner">
            <option value="OFFICE">
              OFFICE
            </option>
            <option value="SHARED">
              SHARED
            </option>
          </select-input>
          <select-input
            v-model="form.project_id"
            :error="form.errors.project_id"
            class="pb-8 pr-6 w-full md:w-1/3"
            label="Sumber Dana (Project)">
            <option
              v-for="project in projects"
              :key="project.id"
              :value="project.id">
              {{ project.title }}
            </option>
          </select-input>
          <select-input
            v-model="form.employee_id"
            :error="form.errors.employee_id"
            class="pb-8 pr-6 w-1/2"
            label="Penanggung Jawab">
            <option
              v-for="employee in employees"
              :key="employee.id"
              :value="employee.id">
              {{ employee.name }}
            </option>
          </select-input>
          <select-input
            v-model="form.purchase_location_id"
            :error="form.errors.purchase_location_id"
            class="pb-8 pr-6 w-full md:w-1/3"
            label="Lokasi Pembelian">
            <option
              v-for="itemLocation in itemLocations"
              :key="itemLocation.id"
              :value="itemLocation.id">
              {{ itemLocation.name }}
            </option>
          </select-input>
          <div class="w-full" />
          <div class="pb-8 pr-6 w-full md:w-1/2">
            <label class="form-label">Harga Beli:</label>
            <number-input v-model="form.purchase_price" :error="form.errors.purchase_price" />
            <div v-if="form.errors.purchase_price" class="form-error whitespace-normal">
              {{ form.errors.purchase_price }}
            </div>
          </div>
          <div class="pb-8 pr-6 w-full md:w-1/3">
            <label class="form-label">Perkiraan Depresiasi Tahunan:</label>
            <number-input
              v-model="form.depreciation_rate"
              :max="0.99"
              :error="form.errors.depreciation_rate" />
            <p class="text-sm text-gray-500">
              *) Pecahan desimal, mis. 0,10 = 10% per tahun. Bukan nominal rupiah.
            </p>
            <div v-if="form.errors.depreciation_rate" class="form-error whitespace-normal">
              {{ form.errors.depreciation_rate }}
            </div>
          </div>
          <textarea-input
            v-model="form.desc"
            :error="form.errors.desc"
            label="Deskripsi Asset"
            class="pb-8 pr-6 w-full md:w-1/2" />
          <div class="flex flex-col pb-8 pr-6 w-full md:w-1/2">
            <label>Tanggal Pengadaan:</label>
            <flat-pickr v-model="form.purchase_date" class="mt-2 p-2 w-full text-center border rounded" />
            <div v-if="form.errors.purchase_date" class="form-error">
              {{ form.errors.purchase_date }}
            </div>
          </div>
        </div>
        <div class="flex items-center justify-end px-8 py-4 bg-gray-50 border-t border-gray-100">
          <loading-button
            :loading="form.processing"
            class="btn-indigo"
            type="submit">
            Create Item Asset
          </loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import Layout from '@/Shared/Layout.vue'

export default {
    layout: Layout,
}
</script>

<script setup>
import { Head, Link, useForm } from '@inertiajs/vue3'
import { ref, defineProps } from 'vue'
import TextInput from '@/Shared/TextInput.vue'
import SelectInput from '@/Shared/SelectInput.vue'
import TextareaInput from '@/Shared/TextareaInput.vue'
import LoadingButton from '@/Shared/LoadingButton.vue'
import NumberInput from '@/Shared/NumberInput'
import { throttle } from 'lodash'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'

const props = defineProps({
    itemAsset: Object,
    projects: Array,
    employees: Array,
    itemLocations: Array,
})

const itemOptions = ref([])
const form = useForm({
    ...props.itemAsset,
})

const fetchItemOptions = throttle((loading, search) => {
    fetch(`/items?options=1&types=STOCK&search=${escape(search)}`).then((res) => {
        res.json().then((json) => (itemOptions.value = json.items))
        loading(false)
    })
}, 500)

const store = () => {
    form.post('/item_assets', {
        preserveScroll: true,
    })
}

function onSearch (search, loading) {
    if (search.length) {
        loading(true)
        fetchItemOptions(loading, search)
    }
}
</script>

<style scoped>
.v-select {
  --vs-line-height: 1.85;
}
</style>
